import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "reactstrap";
import { useGetTenantUsers } from "api/users/service";
import { getReportSchedulePanelBoxStyle } from "./reportScheduleTheme";

function getUserName(user) {
  return user?.user_detail?.full_name || user?.full_name || user?.email || "";
}

const ReportScheduleRecipients = ({ colors, inputStyle, value = [], onChange, fillHeight = false }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const selectedUserIds = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  const { data: usersData, isLoading } = useGetTenantUsers(1, 500);

  const users = useMemo(() => {
    const results = usersData?.results || [];
    return results
      .map((user) => ({
        id: user.id || user.documentId,
        name: getUserName(user),
      }))
      .filter((user) => user.id && user.name)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [usersData]);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => user.name.toLowerCase().includes(query));
  }, [users, searchTerm]);

  const selectedUsers = useMemo(
    () => users.filter((user) => selectedUserIds.includes(user.id)),
    [users, selectedUserIds]
  );

  const toggleUser = (userId) => {
    const next = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];
    onChange?.(next);
  };

  const boxStyle = {
    ...getReportSchedulePanelBoxStyle(colors, { marginTop: 0 }),
    ...(fillHeight ? { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 } : {}),
  };

  const pickerStyle = {
    backgroundColor: colors.fieldBg,
    border: `1px solid ${colors.fieldBorder}`,
  };

  let lastLetter = "";

  return (
    <div
      className={`report-schedule-recipients report-schedule-panel-box${
        fillHeight ? " report-schedule-recipients--fill" : ""
      }`}
      style={boxStyle}
    >
      <h6
        className="mb-3 font-weight-bold report-schedule-panel-title"
        style={{ color: colors.titleColor }}
      >
        {t("reports.schedule.recipientsTitle")}
      </h6>

      <Row
        className={fillHeight ? "flex-fill mb-0" : "mb-0"}
        style={fillHeight ? { minHeight: 0 } : undefined}
      >
        <Col md="6">
          <div className="report-schedule-recipients-picker" style={pickerStyle}>
            <div
              className="report-schedule-recipients-search d-flex align-items-center"
              style={{ borderBottom: `1px solid ${colors.rowBorder}` }}
            >
              <i
                className="fas fa-search"
                style={{ color: colors.mutedColor, marginRight: 8, marginLeft: 12 }}
              />
              <input
                type="text"
                className="form-control border-0 shadow-none report-schedule-recipients-search-input"
                placeholder={t("reports.schedule.searchRecipients")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  ...inputStyle,
                  backgroundColor: "transparent",
                  color: colors.bodyColor,
                }}
              />
            </div>

            <div className="report-schedule-recipients-list">
              {isLoading ? (
                <div
                  className="py-3 px-3"
                  style={{ color: colors.mutedColor, fontSize: 13 }}
                >
                  {t("reports.schedule.loadingRecipients")}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div
                  className="py-3 px-3"
                  style={{ color: colors.mutedColor, fontSize: 13 }}
                >
                  {t("reports.schedule.noRecipientsFound")}
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const letter = user.name.charAt(0).toUpperCase();
                  const showLetter = letter !== lastLetter;
                  lastLetter = letter;

                  return (
                    <div
                      key={user.id}
                      className="report-schedule-recipients-item d-flex align-items-center"
                    >
                      <div
                        className="report-schedule-recipients-letter"
                        style={{ color: colors.mutedColor }}
                      >
                        {showLetter ? letter : ""}
                      </div>
                      <input
                        type="checkbox"
                        id={`recipient_${user.id}`}
                        className="report-schedule-enum-checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUser(user.id)}
                      />
                      <label
                        htmlFor={`recipient_${user.id}`}
                        className="report-schedule-recipients-name mb-0"
                        style={{ color: colors.bodyColor }}
                      >
                        {user.name}
                      </label>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="report-schedule-recipients-selected">
            {selectedUsers.length === 0 ? (
              <div style={{ color: colors.mutedColor, fontSize: 13 }}>
                {t("reports.schedule.noRecipientsSelected")}
              </div>
            ) : (
              selectedUsers.map((user) => (
                <div
                  key={user.id}
                  className="report-schedule-recipients-selected-name"
                  style={{ color: colors.bodyColor }}
                >
                  {user.name}
                </div>
              ))
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ReportScheduleRecipients;
